
import { supabase } from "@/integrations/supabase/client";

export const sendAdminSmsNotification = async (
  eventType: 'signup' | 'booking' | 'cancellation' | 'review',
  data: {
    serviceName?: string;
    userName: string;
    userEmail?: string;
    details?: Record<string, any>;
  }
) => {
  try {
    console.log('Sending admin SMS notification for event:', eventType, data);
    
    // Get admin settings to check if SMS notifications are enabled
    const { data: settingsData, error: settingsError } = await supabase
      .from('admin_settings')
      .select('settings')
      .eq('id', 1)
      .single();

    if (settingsError) {
      console.error('Error fetching admin settings for SMS:', settingsError);
      return false;
    }

    const settings = settingsData?.settings || {};
    const smsSettings = settings.sms || {};

    // Check if SMS notifications are enabled globally and for this event type
    if (!smsSettings.enabled || !smsSettings.notifications?.[`${eventType}s`]) {
      console.log(`SMS notifications for ${eventType} are disabled`);
      return false;
    }

    if (!smsSettings.adminPhone) {
      console.log('Admin phone number not configured for SMS notifications');
      return false;
    }

    // Get the template for this event type
    const template = smsSettings.templates?.[eventType];
    if (!template) {
      console.log(`No SMS template found for ${eventType}`);
      return false;
    }

    // Replace template variables
    let message = template;
    message = message.replace(/\{\{name\}\}/g, data.userName || 'Unknown');
    message = message.replace(/\{\{email\}\}/g, data.userEmail || '');
    message = message.replace(/\{\{service\}\}/g, data.serviceName || '');
    
    if (data.details) {
      message = message.replace(/\{\{date\}\}/g, data.details.date || '');
      message = message.replace(/\{\{time\}\}/g, data.details.time || '');
      message = message.replace(/\{\{rating\}\}/g, data.details.rating || '');
    }

    // Track notification attempt
    try {
      await supabase.from('test_runs').insert({
        family: 'admin_sms_notification',
        type: eventType,
        status: 'pending',
        user_email: data.userEmail || 'unknown',
        user_display_name: data.userName || 'unknown',
        data: {
          details: data,
          message,
          adminPhone: smsSettings.adminPhone,
          timestamp: new Date().toISOString()
        }
      });
    } catch (trackingError) {
      console.error('Error tracking admin SMS notification attempt:', trackingError);
    }

    // Send SMS via edge function using OTP endpoint for reliability
    const { data: response, error } = await supabase.functions.invoke('send-sms', {
      body: {
        partnerID: smsSettings.partnerID || "13693",
        message,
        shortcode: smsSettings.shortcode || "TextSMS",
        mobile: smsSettings.adminPhone,
        endpoint: "otp"
      }
    });
    
    if (error) {
      console.error('Error invoking send-sms function:', error);
      
      // Track error
      try {
        await supabase.from('test_runs').insert({
          family: 'admin_sms_notification',
          type: eventType,
          status: 'error',
          user_email: data.userEmail || 'unknown',
          user_display_name: data.userName || 'unknown',
          data: {
            details: data,
            message,
            error: error.message,
            timestamp: new Date().toISOString()
          }
        });
      } catch (trackingError) {
        console.error('Error tracking admin SMS notification failure:', trackingError);
      }
      
      return false;
    }
    
    console.log('Admin SMS notification response:', response);
    
    // Track success
    try {
      await supabase.from('test_runs').insert({
        family: 'admin_sms_notification',
        type: eventType,
        status: response?.success ? 'success' : 'failed',
        user_email: data.userEmail || 'unknown',
        user_display_name: data.userName || 'unknown',
        data: {
          details: data,
          message,
          response,
          timestamp: new Date().toISOString()
        }
      });
    } catch (trackingError) {
      console.error('Error tracking admin SMS notification result:', trackingError);
    }
    
    return response?.success || false;
    
  } catch (error: any) {
    console.error('Error in sendAdminSmsNotification:', error);
    
    // Track exception
    try {
      await supabase.from('test_runs').insert({
        family: 'admin_sms_notification',
        type: eventType,
        status: 'exception',
        user_email: data.userEmail || 'unknown',
        user_display_name: data.userName || 'unknown',
        data: {
          details: data,
          error: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString()
        }
      });
    } catch (trackingError) {
      console.error('Error tracking admin SMS notification exception:', trackingError);
    }
    
    return false;
  }
};
